import apiFetch from "@/lib/fetch";
import VenueCardSkeleton from "@/components/views/list/card/VenueCardSkeleton";
import VenueClientSection from "@/components/views/list/venue-list/VenueClientSection";
import SeeMoreNearby from "@/components/views/list/see-more/SeeMoreNearby";
import NotFoundNearbyVenues from "@/components/views/list/close-venues/NotFoundNearbyVenues";
import HeaderDataCount from "@/components/views/common/HeaderDataCount";
import { DEFAULT_CACHE_DURATION } from "@/@core/constants/cache";

interface Props {
  service: string;
  district: string;
  venue: string;
}

export default function VenueListSection(props: Props) {
  return (
    // <Suspense fallback={<VenueLoading count={15} />}>
    <VenueSection {...props} />
    // </Suspense>
  );
}

async function getNearbyVenues(props: Props) {
  const searchParams = new URLSearchParams();
  searchParams.set("index", "0");

  let service;
  let district;

  if (props.district) {
    const response = await apiFetch({
      route: `district/${props.district}`,
      revalidate: DEFAULT_CACHE_DURATION,
    });
    if (response?.id) {
      searchParams.set("district", response.id);
      district = response;
    }
  }

  if (props.service) {
    const response = await apiFetch({
      route: `service/${props.service}`,
      revalidate: DEFAULT_CACHE_DURATION,
    });
    if (response?.id) {
      searchParams.set("service", response.id);
      service = response;
    }
  }

  if (!district?.id) return {data: [], service, district}

  const data = await apiFetch({
    route: `search/nearby?${searchParams.toString()}`,
    revalidate: DEFAULT_CACHE_DURATION,
  });

  return {
    data,
    service,
    district,
  };
}

async function VenueSection(props: Props) {
  let url = "search?index=0";

  if (props.service) url += `&service=${props.service}`;
  if (props.district) url += `&district=${props.district}`;
  if (props.venue) url += `&venue=${props.venue}`;

  const venues = await apiFetch({
    route: url,
    revalidate: DEFAULT_CACHE_DURATION,
  });

  const nearbyVenues = await getNearbyVenues(props);
  const nearbyVenuesCount = nearbyVenues?.data.length;

  // Fetch ads server-side. Only the placement matching the visitor's device ends
  // up in the markup - the Ads component drops the other one.
  let filterMobileAds = null;
  let filterDesktopAds = null;

  try {
    const filterMobileParams = new URLSearchParams();
    filterMobileParams.append('placement', 'filter-mobile-mid');

    if (props.service) filterMobileParams.append('serviceSlug', props.service);
    if (props.district) filterMobileParams.append('districtSlug', props.district);

    filterMobileAds = await apiFetch({
      route: `ads?${filterMobileParams.toString()}`,
    });
  } catch (error) {
    console.error('Error fetching filter-mobile-mid ads:', error);
  }

  try {
    const filterDesktopParams = new URLSearchParams();
    filterDesktopParams.append('placement', 'filter-desktop-mid');

    if (props.service) filterDesktopParams.append('serviceSlug', props.service);
    if (props.district) filterDesktopParams.append('districtSlug', props.district);

    filterDesktopAds = await apiFetch({
      route: `ads?${filterDesktopParams.toString()}`,
    });
  } catch (error) {
    console.error('Error fetching filter-desktop-mid ads:', error);
  }

  if (!props.district && nearbyVenuesCount === 0 && !venues.response?.length)
    return (
      <div className="py-4">
        <HeaderDataCount count={venues?.summary} />

        <NotFoundNearbyVenues />
      </div>
    );

  if (props.district && nearbyVenuesCount === 0 && !venues.response?.length)
    return <HeaderDataCount count={venues?.summary} />;

  return (
    <div>
      <HeaderDataCount count={venues?.summary} />

      <VenueClientSection
        {...props}
        data={venues}
        nearbyData={nearbyVenues?.data || []}
        filterMobileAds={filterMobileAds}
        filterDesktopAds={filterDesktopAds}
      />
    </div>
  );
}

export function VenueLoading({ count }: { count: number }) {
  return (
    <div
      id="venue-card-skeleton"
      className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-3 gap-5 py-4"
    >
      {Array(count)
        .fill(0)
        .map((_, index) => {
          return <VenueCardSkeleton key={index} />;
        })}
    </div>
  );
}
